
import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Hero from "@/components/Hero";
import Features from "@/components/Features";
import FeaturedCaregivers from "@/components/FeaturedCaregivers";
import VolunteerSection from "@/components/VolunteerSection";
import CaregiverTracker from "@/components/CaregiverTracker";
import { motion } from "framer-motion";
import { Download, Smartphone, CalendarClock, CheckCircle, Clock, MapPin, 
         Apple, PlayIcon, Bell, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const Index: React.FC = () => {
  const howItWorks = [
    {
      icon: <CalendarClock className="h-6 w-6" />,
      title: "Create Your Bubble",
      description: "Invite family members, trusted caregivers, and community volunteers to join your care bubble."
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      title: "Coordinate Care",
      description: "Use the shared platform to schedule, assign and track care tasks across your bubble members."
    },
    {
      icon: <CheckCircle className="h-6 w-6" />,
      title: "Pool Resources",
      description: "Contribute to your bubble's shared wallet for seamless payment to professional caregivers."
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "Track Progress",
      description: "Monitor care activities in real-time with updates from all bubble members."
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>BubbleCare | Connected Care Networks for Your Loved Ones</title>
        <meta name="description" content="Create your personalized care bubble of trusted caregivers, family members, and local volunteers for comprehensive in-home support and emergency assistance." />
      </Helmet>

      <Hero />
      
      <Features />
      
      {/* How It Works Section - POSITIONED ABOVE FEATURED CAREGIVERS */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute -left-32 top-0 w-64 h-64 bg-care-100 rounded-full opacity-50 blur-3xl" />
        
        <div className="container-custom relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-care-100 text-care-800 mb-4">
                Bubble Management
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-balance">
                How your BubbleCare network works
              </h2>
              <p className="text-muted-foreground text-lg mx-auto text-balance">
                Our platform connects everyone involved in caring for your loved one into a coordinated bubble with centralized communication, scheduling, and resource management.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: [0.45, 0, 0.2, 1]
                }}
              >
                <div className="bg-care-50 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 text-care-600 relative">
                  {step.icon}
                  <div className="absolute -right-1 -top-1 h-6 w-6 bg-care-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-care-800">{step.title}</h3>
                <p className="text-muted-foreground text-balance">{step.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button asChild className="btn-primary">
              <Link to="/bubble">
                Start Your Care Bubble
              </Link>
            </Button>
          </div>
        </div>
      </section>
      
      {/* Featured Caregivers Section - NOW POSITIONED BELOW HOW IT WORKS */}
      <FeaturedCaregivers />
      
      {/* Live Caregiver Tracking Demo */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute -left-32 bottom-0 w-64 h-64 bg-care-100 rounded-full opacity-50 blur-3xl" />
        
        <div className="container-custom relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-care-100 text-care-800 mb-4">
                Bubble Transparency
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-balance">
                Real-time visibility for your entire bubble
              </h2>
              <p className="text-muted-foreground text-lg mx-auto text-balance">
                Everyone in your care bubble can track caregivers in real-time as they travel to your loved one's location, providing peace of mind and precise arrival estimates for all involved.
              </p>
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-care-100 rounded-lg text-care-600">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-care-800">Bubble-wide updates</h3>
                    <p className="text-muted-foreground">All bubble members can monitor caregiver arrival in real-time.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-care-100 rounded-lg text-care-600">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-care-800">Shared location tracking</h3>
                    <p className="text-muted-foreground">Collective visibility of caregiver location for all bubble members.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-care-100 rounded-lg text-care-600">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-care-800">Group communication</h3>
                    <p className="text-muted-foreground">All bubble members can communicate with the caregiver simultaneously.</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Button asChild className="btn-primary">
                  <Link to="/bubble">Manage Your Bubble</Link>
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="order-1 lg:order-2 max-w-md mx-auto"
            >
              <CaregiverTracker 
                caregiverId="cg-456"
                caregiverName="Robert Chen"
                caregiverImage="https://source.unsplash.com/7YVZYZeITc8/400x400"
                estimatedArrival="10:45 AM today"
              />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Download App Section - UPDATED */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white relative overflow-hidden">
        <div className="absolute -right-32 top-0 w-64 h-64 bg-blue-100 rounded-full opacity-50 blur-3xl" />
        <div className="absolute -left-32 bottom-0 w-64 h-64 bg-blue-100 rounded-full opacity-50 blur-3xl" />
        
        <div className="container-custom relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-4">
                Mobile Experience
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-balance">
                Take BubbleCare with you everywhere
              </h2>
              <p className="text-muted-foreground text-lg mx-auto text-balance">
                Stay connected to your care bubble on the go. Our intuitive mobile app gives you instant access to your entire care network, real-time updates, and emergency assistance from any device.
              </p>
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="order-2 lg:order-1"
            >
              <div className="relative mx-auto max-w-[280px]">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-100 to-blue-50 rounded-3xl transform -rotate-6" />
                <Card className="relative rounded-3xl border-2 border-blue-100 overflow-hidden shadow-xl">
                  <CardContent className="p-2">
                    <img
                      src="https://source.unsplash.com/photo-1649972904349-6e44c42644a7"
                      alt="BubbleCare mobile app"
                      className="rounded-2xl aspect-[9/16] object-cover"
                    />
                  </CardContent>
                </Card>
                
                <div className="absolute -bottom-6 -right-6 bg-white rounded-full p-3 shadow-lg border border-gray-100">
                  <div className="bg-blue-50 h-16 w-16 rounded-full flex items-center justify-center text-blue-600">
                    <Smartphone className="h-8 w-8" />
                  </div>
                </div>
                
                <div className="absolute -top-6 -left-6 bg-white rounded-full p-2 shadow-lg border border-gray-100">
                  <div className="bg-blue-50 h-10 w-10 rounded-full flex items-center justify-center text-blue-600">
                    <Bell className="h-5 w-5" />
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-blue-800">Real-time notifications</h3>
                    <p className="text-muted-foreground">Get instant alerts when caregivers check in or when tasks are completed.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-blue-800">Location tracking</h3>
                    <p className="text-muted-foreground">Track caregiver location in real-time and get estimated arrival times.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-blue-800">Secure communication</h3>
                    <p className="text-muted-foreground">End-to-end encrypted messaging with your entire care bubble.</p>
                  </div>
                </div>
                
                <div className="mt-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <motion.div
                      whileHover={{ y: -5 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <Button variant="default" className="w-full bg-black hover:bg-gray-800 text-white flex gap-2 h-14 rounded-xl">
                        <Apple className="h-6 w-6" />
                        <div className="text-left">
                          <div className="text-xs">Download on the</div>
                          <div className="font-medium">App Store</div>
                        </div>
                      </Button>
                    </motion.div>
                    
                    <motion.div
                      whileHover={{ y: -5 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <Button variant="default" className="w-full bg-black hover:bg-gray-800 text-white flex gap-2 h-14 rounded-xl">
                        <PlayIcon className="h-6 w-6" />
                        <div className="text-left">
                          <div className="text-xs">Get it on</div>
                          <div className="font-medium">Google Play</div>
                        </div>
                      </Button>
                    </motion.div>
                  </div>
                  
                  <div className="mt-4 text-center">
                    <Link to="/bubble" className="text-blue-600 text-sm hover:underline">
                      Or use the web version →
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          <div className="mt-20 pt-8 border-t border-blue-100">
            <div className="flex flex-wrap justify-center gap-8 items-center">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-800">50k+</div>
                <div className="text-muted-foreground">App Downloads</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-800">4.8/5</div>
                <div className="text-muted-foreground">App Store Rating</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-800">4.7/5</div>
                <div className="text-muted-foreground">Google Play Rating</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-800">18k+</div>
                <div className="text-muted-foreground">Active Daily Users</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <VolunteerSection />
      
      {/* CTA Section */}
      <section className="py-20 bg-care-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-white rounded-full" />
          <div className="absolute top-40 right-20 w-40 h-40 bg-white rounded-full" />
          <div className="absolute -bottom-40 left-40 w-80 h-80 bg-white rounded-full" />
        </div>
        
        <div className="container-custom relative text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight text-balance">
              Ready to create your care bubble?
            </h2>
            <p className="text-lg opacity-90 mb-8 mx-auto text-balance">
              Join thousands of families who trust BubbleCare to build and coordinate care networks that provide reliable, compassionate support for their elderly family members.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button asChild className="bg-white text-care-700 hover:bg-gray-100 font-medium py-3 px-6 rounded-full">
                <Link to="/bubble">
                  Create Your Bubble
                </Link>
              </Button>
              <Button variant="outline" className="text-white border-white hover:bg-care-700 font-medium py-3 px-6 rounded-full">
                Learn More
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
